import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'user-stats';
const ACHIEVEMENTS_KEY = 'user-achievements';
const LAST_STUDY_KEY = 'last-study-date';

export function useGameStats(userId = 'default') {
  const [stats, setStats] = useState({
    totalPoints: 0,
    currentStreak: 0,
    bestStreak: 0,
    modulesCompleted: 0,
    quizzesCompleted: 0,
    perfectQuizzes: 0,
    flashcardsMastered: 0,
    studyTime: 0,
    level: 1,
    lastStudyDate: null,
  });

  useEffect(() => {
    // Load stats from localStorage
    const savedStats = localStorage.getItem(`${STORAGE_KEY}-${userId}`);
    const lastStudyDate = localStorage.getItem(`${LAST_STUDY_KEY}-${userId}`);
    
    if (savedStats) {
      const parsedStats = JSON.parse(savedStats);
      setStats(parsedStats);
      
      // Check and update streak
      if (lastStudyDate) {
        const lastDate = new Date(lastStudyDate);
        const today = new Date();
        const diffTime = today - lastDate;
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays > 1) {
          // Streak broken
          setStats(prev => ({ ...prev, currentStreak: 0 }));
        } else if (diffDays === 1) {
          // Continue streak
          setStats(prev => ({
            ...prev,
            currentStreak: prev.currentStreak + 1,
            bestStreak: Math.max(prev.bestStreak, prev.currentStreak + 1),
          }));
        }
      }
    }
    
    // Update last study date
    localStorage.setItem(`${LAST_STUDY_KEY}-${userId}`, new Date().toISOString());
  }, [userId]);

  const saveStats = useCallback((newStats) => {
    localStorage.setItem(`${STORAGE_KEY}-${userId}`, JSON.stringify(newStats));
  }, [userId]);

  const updateStats = useCallback((updates) => {
    setStats(prev => {
      const newStats = { ...prev, ...updates };
      saveStats(newStats);
      return newStats;
    });
  }, [saveStats]);

  const incrementModulesCompleted = useCallback(() => {
    updateStats({
      modulesCompleted: stats.modulesCompleted + 1,
      totalPoints: stats.totalPoints + 50,
    });
  }, [stats, updateStats]);

  const recordQuizScore = useCallback((score, totalQuestions) => {
    const percentage = (score / totalQuestions) * 100;
    const isPerfect = percentage === 100;
    const points = Math.round(percentage * 2); // 2 points per percentage
    
    updateStats({
      quizzesCompleted: stats.quizzesCompleted + 1,
      perfectQuizzes: isPerfect ? stats.perfectQuizzes + 1 : stats.perfectQuizzes,
      totalPoints: stats.totalPoints + points,
    });
  }, [stats, updateStats]);

  const incrementFlashcardsMastered = useCallback((count = 1) => {
    updateStats({
      flashcardsMastered: stats.flashcardsMastered + count,
      totalPoints: stats.totalPoints + (count * 5), // 5 points per flashcard
    });
  }, [stats, updateStats]);

  const addStudyTime = useCallback((minutes) => {
    updateStats({
      studyTime: stats.studyTime + minutes,
      totalPoints: stats.totalPoints + Math.floor(minutes / 10), // 1 point per 10 minutes
    });
  }, [stats, updateStats]);

  const checkDailyStreak = useCallback(() => {
    const lastStudyDate = localStorage.getItem(`${LAST_STUDY_KEY}-${userId}`);
    const today = new Date().toDateString();
    
    if (!lastStudyDate || new Date(lastStudyDate).toDateString() !== today) {
      localStorage.setItem(`${LAST_STUDY_KEY}-${userId}`, new Date().toISOString());
      return true; // First study of the day
    }
    return false;
  }, [userId]);

  return {
    stats,
    updateStats,
    incrementModulesCompleted,
    recordQuizScore,
    incrementFlashcardsMastered,
    addStudyTime,
    checkDailyStreak,
  };
}